import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { FolderKanban, BadgeCheck, Radio, MessageSquare, BookOpen, CalendarDays, Clock3, CreditCard, Package, ReceiptText, Search, Sparkles, UsersRound, PhoneCall, BrainCircuit } from "lucide-react";
import type { WorkspaceNavId } from "../lib/workspace-navigation";
import "./workspace-icon-rail.css";

const icons = {
  projects: FolderKanban,
  texts: MessageSquare,
  communications: Radio,
  search: Search,
  agent: Sparkles,
  booking_agent: PhoneCall,
  learning: BookOpen,
  dispatch: CalendarDays,
  timesheets: Clock3,
  customers: UsersRound,
  memberships: BadgeCheck,
  pricebook: ReceiptText,
  inventory: Package,
  commercial: CreditCard,
  foundry_analysis: BrainCircuit,
};

export function WorkspaceIconRail({ items, isActive, onSelect, unreadTexts = 0 }: {
  unreadTexts?: number;
  items: Array<{ id: WorkspaceNavId; label: string }>;
  isActive: (id: WorkspaceNavId) => boolean;
  onSelect: (id: WorkspaceNavId) => void;
}) {
  const tooltipId = useId();
  const [tooltip, setTooltip] = useState<{ id: WorkspaceNavId; label: string; left: number; top: number } | null>(null);
  useEffect(() => {
    if (!tooltip) return;
    const dismiss = () => setTooltip(null);
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") dismiss(); };
    window.addEventListener("resize", dismiss);
    window.addEventListener("scroll", dismiss, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", dismiss);
      window.removeEventListener("scroll", dismiss, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [Boolean(tooltip)]);

  function showLabel(button: HTMLButtonElement, item: typeof items[number]) {
    const bounds = button.getBoundingClientRect();
    setTooltip({ ...item, left: bounds.right + 12, top: Math.max(20, Math.min(window.innerHeight - 20, bounds.top + bounds.height / 2)) });
  }

  return (
    <aside className="workspace-icon-rail">
      <nav aria-label="Workspace navigation" className="workspace-icon-rail-scroll">
        <ul>
          {items.map((item) => {
            const Icon = icons[item.id];
            return (
              <li key={item.id}>
                <button
                  type="button"
                  data-workspace-widget-trigger={item.id === "texts" ? "texts" : undefined}
                  aria-label={item.label}
                  aria-current={isActive(item.id) ? "page" : undefined}
                  aria-describedby={tooltip?.id === item.id ? tooltipId : undefined}
                  className="workspace-icon-rail-button"
                  onPointerEnter={(event) => { if (event.pointerType !== "touch") showLabel(event.currentTarget, item); }}
                  onPointerLeave={() => setTooltip(null)}
                  onFocus={(event) => showLabel(event.currentTarget, item)}
                  onBlur={() => setTooltip(null)}
                  onClick={() => { setTooltip(null); onSelect(item.id); }}
                >
                  <Icon size={17} strokeWidth={1.7} aria-hidden="true" />
                  {item.id === "texts" && unreadTexts > 0 && <span className="workspace-icon-rail-count" aria-label={`${unreadTexts} unread ${unreadTexts === 1 ? "text" : "texts"}`}>{unreadTexts > 99 ? "99+" : unreadTexts}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>
      {tooltip && createPortal(
        <span id={tooltipId} role="tooltip" className="workspace-icon-rail-tooltip" style={{ left: tooltip.left, top: tooltip.top }}>
          {tooltip.label}
        </span>,
        document.body,
      )}
    </aside>
  );
}
